import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { SelectorComponent } from "@components/selector/selector.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { orderBy } from "lodash";
import { CommandControlBinder } from "~app/common/services/cqs/command-control-binder/command-control-binder";
import { CommandService } from "~app/common/services/cqs/command.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { TransporttilldelaArbetsorderCommand } from "~app/features/transport/transportplanera/models/commands";
import { ITransportforetagForTilldelningQueryResult } from "~app/features/transport/transportplanera/models/queries";
import { TplArbetsorderViewModel } from "~app/features/transport/transportplanera/models/tplListViewModels";
import { ValidationMessageRowComponent } from "../../../../../../../common/validation/validation-message-row/validation-message-row.component";

@Component({
    selector: "rask-tpl-transporttilldelning-ao",
    templateUrl: "./tpl-transporttilldelning-ao.component.html",
    styleUrls: ["./tpl-transporttilldelning-ao.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ValidationMessageRowComponent,
        FormsModule,
        ReactiveFormsModule,
        SelectorComponent,
        TranslatePipe
    ]
})
export class TplTransporttilldelningAoComponent implements OnInit {
    @Input() arbetsordrar: TplArbetsorderViewModel[];
    command: TransporttilldelaArbetsorderCommand;
    deladeArbetsordrar: string[] = [];
    @Input() transportforetag: ITransportforetagForTilldelningQueryResult[];
    form = new UntypedFormGroup({
        transportforetag: new UntypedFormControl("")
    });
    isSelectorInFocus = false;

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onArbetsorderTilldelad: EventEmitter<UntypedFormGroup> = new EventEmitter();
    @Input() selectedTransportforetag: number;
    transportforetagAo: ITransportforetagForTilldelningQueryResult[];
    warningAoNotApplicable: string = null;

    private readonly commandService = inject(CommandService);
    private readonly translateService = inject(TranslateService);

    bindControlToCommand() {
        const arbetsorderIds = this.getArbetsorderIds();

        const binder = new CommandControlBinder("", this.commandService, TransporttilldelaArbetsorderCommand);
        const ctrls = this.form.controls;

        binder.bindControl(ctrls.transportforetag, (v, c) => {
            c.arbetsorderIds = arbetsorderIds;
            c.transportforetagId = v;
        });
    }

    initWarnings() {
        if (this.arbetsordrar.length > 1) {
            this.arbetsordrar.forEach(ao => {
                if (ao.isDelad) {
                    this.deladeArbetsordrar.push(ao.beteckning);
                }
            });
        }

        this.arbetsordrar.forEach(ao => {
            const isTfExtern = this.transportforetag.find(x => x.id === ao.transportforetagId)?.transportunderlagTillExtern;

            // Kolla även på om det finns en aktiv aktiv AktivVirkesorderVals
            if (isTfExtern && ao.harAktivVirkesorderVals) {
                ao.showWarning = true;
            }
        });

        this.warningToShow();
    }

    ngOnInit(): void {
        if (!ObjectUtils.hasValue(this.selectedTransportforetag)) {
            this.selectedTransportforetag = -1;
        }

        this.form.get("transportforetag").setValue(this.selectedTransportforetag);
        this.transportforetagAo = orderBy(this.transportforetag.filter(x => x.aktiv || x.id === this.selectedTransportforetag), x => this.tplFormatter(x));

        this.bindControlToCommand();
        this.initWarnings();
        this.onTransportforetagChanges();
    }

    onTransportforetagChanges(): void {
        this.form.get("transportforetag").valueChanges.subscribe(val => {
            const foretag = this.form.get("transportforetag")?.value;

            if (!ObjectUtils.isNullOrUndefined(foretag)) {
                this.onArbetsorderTilldelad.emit(this.form);
            }
        });
    }

    setTransportForetag(transportforetagId) {
        if (typeof transportforetagId !== "number" && transportforetagId !== undefined) {
            return;
        }

        this.toggleExternTransportforetagWarning(transportforetagId);
        this.warningToShow();
    }

    tplFormatter = (result: ITransportforetagForTilldelningQueryResult) => result ? `${result.huvudkod} ${result.namn}` : undefined;

    tplOutputFormatter = (result: ITransportforetagForTilldelningQueryResult) => result ? result.id : undefined;

    private getArbetsorderIds(): number[] {
        if (this.arbetsordrar.length === 1) {
            return this.arbetsordrar.map(x => x.id);
        } else {
            return this.arbetsordrar.filter(x => !x.isDelad).map(x => x.id);
        }
    }

    private toggleExternTransportforetagWarning(event) {
        const isTfExtern = this.transportforetag.find(x => x.id === event)?.transportunderlagTillExtern;
        const warningAoIdList = this.arbetsordrar.filter(x => x.showWarning).map(x => x.id);

        this.arbetsordrar.forEach(ao => {
            const aoOrgWarning = ObjectUtils.hasValue(warningAoIdList.find(x => x === ao.id));
            if ((!ao.isDelad && !aoOrgWarning || this.arbetsordrar.length === 1 && !aoOrgWarning)) {
                ao.showWarning = isTfExtern && ao.harAktivVirkesorderVals;
            }
        });
    }

    private warningToShow() {
        this.warningAoNotApplicable = null;
        if (this.arbetsordrar.length === 1 && this.arbetsordrar[0].showWarning) {
            this.warningAoNotApplicable = this.translateService.instant("TPL_TRANSPORTTILLDELA_ANDRING_VO_VALS");
            return;
        }

        const warningAos = this.arbetsordrar.filter(x => x.showWarning).map(ao => ao.beteckning);
        if (warningAos.length > 0) {
            this.warningAoNotApplicable = `${this.translateService.instant("TPL_TRANSPORTTILLDELA_ANDRING_VO_VALS_MULTIPLE")} ${warningAos.join(", ")}`;
        }
    }
}
